import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { updateAdminProvider, type AdminProvider } from "@/lib/admin";

/**
 * Admin-side edit of a provider's details — the same field set the provider can change
 * on itself. The API overwrites every field it receives, so the form is seeded with the
 * current values and always submits all of them; sending a partial body would blank the
 * columns left out.
 */
export default function EditProviderForm({
  provider, onSaved, onCancel,
}: {
  provider: AdminProvider;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation("admin");
  const [form, setForm] = useState({
    companyName: provider.companyName ?? "",
    rfc: provider.rfc ?? "",
    location: provider.location ?? "",
    contactPhone: provider.contactPhone ?? "",
    website: provider.website ?? "",
    description: provider.description ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.companyName.trim()) {
      setError(t("admin.prov.nameRequired", { defaultValue: "El nombre de la empresa es obligatorio." }));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await updateAdminProvider(provider.id, {
        companyName: form.companyName.trim(),
        rfc: form.rfc.trim() || null,
        location: form.location.trim() || null,
        contactPhone: form.contactPhone.trim() || null,
        website: form.website.trim() || null,
        description: form.description.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("admin.error", { defaultValue: "Ocurrió un error." }));
    } finally {
      setSaving(false);
    }
  };

  const input = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-ocean";
  const label = "block text-xs font-medium text-gray-500 mb-1";

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-gray-100 p-4 my-2">
      {error && <p className="text-xs text-coral mb-3">{error}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div>
          <label className={label}>{t("admin.prov.company", { defaultValue: "Empresa" })}</label>
          <input type="text" value={form.companyName} onChange={(e) => set("companyName", e.target.value)} maxLength={160} className={input} />
        </div>
        <div>
          <label className={label}>{t("admin.prov.rfc", { defaultValue: "RFC" })}</label>
          <input type="text" value={form.rfc} onChange={(e) => set("rfc", e.target.value)} maxLength={20} className={input} />
        </div>
        <div>
          <label className={label}>{t("admin.prov.location", { defaultValue: "Ubicación" })}</label>
          <input type="text" value={form.location} onChange={(e) => set("location", e.target.value)} maxLength={200} className={input} />
        </div>
        <div>
          <label className={label}>{t("admin.prov.phone", { defaultValue: "Teléfono de contacto" })}</label>
          <input type="text" value={form.contactPhone} onChange={(e) => set("contactPhone", e.target.value)} maxLength={40} className={input} />
        </div>
        <div>
          <label className={label}>{t("admin.prov.website", { defaultValue: "Sitio web" })}</label>
          <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} maxLength={200} className={input} />
        </div>
      </div>
      <div className="mt-3">
        <label className={label}>{t("admin.prov.description", { defaultValue: "Descripción" })}</label>
        <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} maxLength={2000} className={`${input} resize-none`} />
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button
          type="submit"
          disabled={saving}
          className="px-4 py-2 rounded-full bg-ocean text-white text-xs font-medium transition-colors hover:bg-ocean/90 disabled:opacity-60"
        >
          {saving ? t("admin.saving", { defaultValue: "Guardando…" }) : t("admin.save", { defaultValue: "Guardar" })}
        </button>
        <button type="button" onClick={onCancel} className="text-xs font-medium text-gray-500 hover:text-charcoal">
          {t("admin.cancel", { defaultValue: "Cancelar" })}
        </button>
      </div>
    </form>
  );
}
